import type { Dispatch, SetStateAction } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';
import type {
  ChatChoiceOptionschema,
  ChatFormFieldschema,
  ChatFormResponseEntryschema,
  ChatInteractRequestContentBlock as ApiChatInteractRequestContentBlock,
  ChatMessageContentBlock as ApiChatMessageContentBlock
} from '@reap/openapi-public-v1';

export type ChatChoiceOptionRecord = ChatChoiceOptionschema;
export type ChatFormFieldRecord = ChatFormFieldschema;
export type ChatFormResponseFieldRecord = ChatFormResponseEntryschema;
export type ChatMessageContentBlock = ApiChatMessageContentBlock;
export type ChatInteractRequestContentBlock = ApiChatInteractRequestContentBlock;
export type ChatChoicePromptBlock = Extract<ChatMessageContentBlock, { type: 'choicePrompt' }>;
export type ChatChoiceResponseBlock = Extract<ChatMessageContentBlock, { type: 'choiceResponse' }>;
export type ChatConfirmationPromptBlock = Extract<ChatMessageContentBlock, { type: 'confirmationPrompt' }>;
export type ChatConfirmationResponseBlock = Extract<ChatMessageContentBlock, { type: 'confirmationResponse' }>;
export type ChatInputPromptBlock = Extract<ChatMessageContentBlock, { type: 'inputPrompt' }>;
export type ChatInputResponseBlock = Extract<ChatMessageContentBlock, { type: 'inputResponse' }>;
export type ChatFormPromptBlock = Extract<ChatMessageContentBlock, { type: 'formPrompt' }>;
export type ChatFormResponseBlock = Extract<ChatMessageContentBlock, { type: 'formResponse' }>;
export type ChatResponseContentBlock =
  | ChatChoiceResponseBlock
  | ChatConfirmationResponseBlock
  | ChatInputResponseBlock
  | ChatFormResponseBlock;

export interface PromptChoiceDraft {
  selectedIds: string[];
  useFreeform: boolean;
  freeformText: string;
}

export type PromptChoiceDraftMap = Record<string, PromptChoiceDraft>;
export type PromptInputDraftMap = Record<string, string>;
export type PromptFormDraftMap = Record<string, Record<string, string>>;
export type PromptSubmissionStateMap = Record<string, { pending: boolean; error?: string }>;

export interface ChatMessageContentEvent {
  id: string;
  messageId?: string;
  role?: string;
  type?: string;
  text?: string;
  runId?: string;
  artifacts?: Array<{ id: string; runId?: string; summary?: string; kind?: string; mimeType?: string }>;
  contentBlocks?: ChatMessageContentBlock[];
}

interface ChatMessageContentBlocksProps {
  event: ChatMessageContentEvent;
  answeredPromptKeys: Set<string>;
  supersededPromptKeys: Set<string>;
  latestResponsesByPromptKey: Record<string, ChatResponseContentBlock>;
  promptChoiceDrafts: PromptChoiceDraftMap;
  promptInputDrafts: PromptInputDraftMap;
  promptFormDrafts: PromptFormDraftMap;
  promptSubmissionState: PromptSubmissionStateMap;
  setPromptChoiceDrafts: Dispatch<SetStateAction<PromptChoiceDraftMap>>;
  setPromptInputDrafts: Dispatch<SetStateAction<PromptInputDraftMap>>;
  setPromptFormDrafts: Dispatch<SetStateAction<PromptFormDraftMap>>;
  submitSingleChoicePrompt: (
    event: ChatMessageContentEvent,
    block: ChatChoicePromptBlock,
    blockIndex: number,
    option: ChatChoiceOptionRecord
  ) => Promise<void> | void;
  submitChoicePromptDraft: (event: ChatMessageContentEvent, block: ChatChoicePromptBlock, blockIndex: number) => Promise<void> | void;
  submitConfirmationPrompt: (
    event: ChatMessageContentEvent,
    block: ChatConfirmationPromptBlock,
    blockIndex: number,
    confirmed: boolean
  ) => Promise<void> | void;
  submitInputPrompt: (event: ChatMessageContentEvent, block: ChatInputPromptBlock, blockIndex: number) => Promise<void> | void;
  submitFormPrompt: (event: ChatMessageContentEvent, block: ChatFormPromptBlock, blockIndex: number) => Promise<void> | void;
  transformMarkdownText?: (text: string, event: ChatMessageContentEvent) => string;
  markdownComponents?: Components;
  markdownUrlTransform?: (url: string, key: string, node: Readonly<unknown>) => string;
}

export const formatFormResponseFieldValue = (field: ChatFormResponseFieldRecord): string => {
  if (typeof field.valueText === 'string') {
    return field.valueText.trim();
  }
  if (typeof field.valueNumber === 'number') {
    return String(field.valueNumber);
  }
  if (typeof field.valueBoolean === 'boolean') {
    return field.valueBoolean ? 'Yes' : 'No';
  }
  if (typeof field.valueChoiceId === 'string') {
    return field.valueChoiceId.trim();
  }
  if (Array.isArray(field.valueChoiceIds)) {
    return field.valueChoiceIds.join(', ').trim();
  }
  return '';
};

export const resolveChatMessageText = (message: { content?: ChatMessageContentBlock[] }): string => {
  const text = (message.content ?? [])
    .map((segment) => {
      switch (segment.type) {
        case 'text':
          return segment.text ?? '';
        case 'inputResponse':
          return segment.text ?? '';
        case 'choiceResponse':
          return segment.text ?? segment.label ?? (segment.labels ?? []).join(', ');
        case 'confirmationResponse':
          return segment.decision === 'confirm' ? 'Confirm' : segment.decision === 'cancel' ? 'Cancel' : '';
        case 'formResponse': {
          const fields = Array.isArray(segment.fields) ? (segment.fields as ChatFormResponseFieldRecord[]) : [];
          return fields
            .map((field) => formatFormResponseFieldValue(field))
            .filter(Boolean)
            .join('\n');
        }
        default:
          return '';
      }
    })
    .filter(Boolean)
    .join('\n')
    .trim();
  return text || '';
};

export const isResponseBlock = (block: ChatMessageContentBlock): block is ChatResponseContentBlock =>
  block.type === 'choiceResponse' ||
  block.type === 'confirmationResponse' ||
  block.type === 'inputResponse' ||
  block.type === 'formResponse';

export const getPromptKey = (messageId: string | undefined, questionId: string | undefined, blockType: string | undefined, blockIndex: number): string =>
  `${messageId ?? 'message'}:${questionId ?? 'prompt'}:${blockType ?? 'block'}:${blockIndex}`;

export const getPromptMessageId = (event: Pick<ChatMessageContentEvent, 'id' | 'messageId'>): string | undefined => event.messageId ?? event.id;

type ChatPromptBlock = ChatChoicePromptBlock | ChatConfirmationPromptBlock | ChatInputPromptBlock | ChatFormPromptBlock;

export interface PromptAnswerState {
  answeredPromptKeys: Set<string>;
  supersededPromptKeys: Set<string>;
  latestResponsesByPromptKey: Record<string, ChatResponseContentBlock>;
}

type PromptAnswerEvent = Pick<ChatMessageContentEvent, 'id' | 'messageId' | 'role' | 'type' | 'contentBlocks'>;

const isPromptBlock = (block: ChatMessageContentBlock): block is ChatPromptBlock =>
  block.type === 'choicePrompt' ||
  block.type === 'confirmationPrompt' ||
  block.type === 'inputPrompt' ||
  block.type === 'formPrompt';

const promptTypeForResponse = (block: ChatResponseContentBlock): ChatPromptBlock['type'] | undefined => {
  switch (block.type) {
    case 'choiceResponse':
      return 'choicePrompt';
    case 'confirmationResponse':
      return 'confirmationPrompt';
    case 'inputResponse':
      return 'inputPrompt';
    case 'formResponse':
      return 'formPrompt';
  }
};

const promptCorrelationKey = (questionId: string, promptType: ChatPromptBlock['type']): string => `${questionId}:${promptType}`;

// A response carries only a stable question ID, not the originating prompt
// message ID. For duplicate unresolved prompts, only the newest may remain
// actionable; this avoids assigning a response to a different prompt card.
export const getPromptAnswerState = (events: PromptAnswerEvent[]): PromptAnswerState => {
  const unansweredPromptKeys = new Map<string, string>();
  const answeredPromptKeys = new Set<string>();
  const supersededPromptKeys = new Set<string>();
  const latestResponsesByPromptKey: Record<string, ChatResponseContentBlock> = {};

  events.forEach((event) => {
    if (event.type && event.type !== 'message') {
      return;
    }
    const blocks = event.contentBlocks ?? [];
    if (event.role === 'assistant') {
      blocks.forEach((block, blockIndex) => {
        if (!isPromptBlock(block)) {
          return;
        }
        const questionId = (block.questionId ?? '').trim();
        if (!questionId) {
          return;
        }
        const correlation = promptCorrelationKey(questionId, block.type);
        const promptKey = getPromptKey(getPromptMessageId(event), questionId, block.type, blockIndex);
        const priorPromptKey = unansweredPromptKeys.get(correlation);
        // Response blocks carry only a logical question ID, so they cannot
        // safely distinguish two simultaneous prompt instances. Keep the
        // newest prompt actionable and retire the older unresolved one rather
        // than letting a response be attributed to the wrong card.
        if (priorPromptKey && priorPromptKey !== promptKey) {
          supersededPromptKeys.add(priorPromptKey);
        }
        unansweredPromptKeys.set(correlation, promptKey);
      });
      return;
    }
    if (event.role !== 'user') {
      return;
    }
    blocks.forEach((block) => {
      if (!isResponseBlock(block)) {
        return;
      }
      const questionId = (block.questionId ?? '').trim();
      const promptType = promptTypeForResponse(block);
      if (!questionId || !promptType) {
        return;
      }
      const correlation = promptCorrelationKey(questionId, promptType);
      const promptKey = unansweredPromptKeys.get(correlation);
      if (!promptKey) {
        return;
      }
      unansweredPromptKeys.delete(correlation);
      answeredPromptKeys.add(promptKey);
      latestResponsesByPromptKey[promptKey] = block;
    });
  });

  return { answeredPromptKeys, supersededPromptKeys, latestResponsesByPromptKey };
};

const buildSelectionRuleText = (
  selectionMode: 'single' | 'multi',
  minSelections?: number,
  maxSelections?: number
): string | null => {
  const explicitMin = typeof minSelections === 'number' ? minSelections : undefined;
  const explicitMax = typeof maxSelections === 'number' ? maxSelections : undefined;
  const singleDefault =
    selectionMode === 'single' &&
    (explicitMin === undefined || explicitMin === 1) &&
    (explicitMax === undefined || explicitMax === 1);
  const multiDefault = selectionMode === 'multi' && explicitMin === undefined && explicitMax === undefined;
  if (singleDefault || multiDefault) {
    return null;
  }
  if (explicitMin !== undefined && explicitMax !== undefined) {
    if (explicitMin === explicitMax) {
      return `Select exactly ${explicitMin} option${explicitMin === 1 ? '' : 's'}.`;
    }
    return `Select between ${explicitMin} and ${explicitMax} options.`;
  }
  if (explicitMin !== undefined) {
    return `Select at least ${explicitMin} option${explicitMin === 1 ? '' : 's'}.`;
  }
  if (explicitMax !== undefined) {
    return `Select up to ${explicitMax} option${explicitMax === 1 ? '' : 's'}.`;
  }
  return null;
};

export function ChatMessageContentBlocks({
  event,
  answeredPromptKeys,
  supersededPromptKeys,
  latestResponsesByPromptKey,
  promptChoiceDrafts,
  promptInputDrafts,
  promptFormDrafts,
  promptSubmissionState,
  setPromptChoiceDrafts,
  setPromptInputDrafts,
  setPromptFormDrafts,
  submitSingleChoicePrompt,
  submitChoicePromptDraft,
  submitConfirmationPrompt,
  submitInputPrompt,
  submitFormPrompt,
  transformMarkdownText,
  markdownComponents,
  markdownUrlTransform
}: ChatMessageContentBlocksProps) {
  const renderMarkdown = (text: string) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={markdownComponents}
      urlTransform={markdownUrlTransform ?? defaultUrlTransform}>
      {transformMarkdownText ? transformMarkdownText(text, event) : text}
    </ReactMarkdown>
  );
  const blocks = Array.isArray(event.contentBlocks) ? event.contentBlocks : [];
  const promptMessageId = getPromptMessageId(event);
  const nodes = blocks
    .map((block, index) => {
      if (block.type === 'text') {
        const text = block.text?.trim() ?? '';
        if (!text) {
          return null;
        }
        return (
          <div key={`${event.id}-block-${index}`} className="chat-message__markdown">
            {renderMarkdown(text)}
          </div>
        );
      }

      if (block.type === 'code') {
        const codeText = 'code' in block && typeof block.code === 'string' ? block.code : '';
        return (
          <pre key={`${event.id}-block-${index}`} className="chat-message__code">
            <code>{codeText}</code>
          </pre>
        );
      }

      if (block.type === 'infoCard') {
        const title = typeof block.title === 'string' ? block.title.trim() : '';
        const body = typeof block.body === 'string' ? block.body.trim() : '';
        const severity = typeof block.severity === 'string' ? block.severity.trim().toLowerCase() : '';
        if (!title && !body) {
          return null;
        }
        return (
          <div
            key={`${event.id}-block-${index}`}
            className={`chat-info-card${severity ? ` chat-info-card--${severity}` : ''}`}>
            {title && <div className="chat-info-card__title">{title}</div>}
            {body && (
              <div className="chat-info-card__body">
                {renderMarkdown(body)}
              </div>
            )}
          </div>
        );
      }

      if (block.type === 'choicePrompt') {
        const promptKey = getPromptKey(promptMessageId, block.questionId, block.type, index);
        const submission = promptSubmissionState[promptKey];
        const isAnswered = answeredPromptKeys.has(promptKey);
        const isSuperseded = supersededPromptKeys.has(promptKey);
        const isPending = Boolean(submission?.pending);
        const choices = Array.isArray(block.choices) ? block.choices : [];
        const selectionMode = block.selectionMode === 'multi' ? 'multi' : 'single';
        const inputType = selectionMode === 'multi' ? 'checkbox' : 'radio';
        const latestResponse = latestResponsesByPromptKey[promptKey];
        const responseChoiceIds =
          latestResponse?.type === 'choiceResponse'
            ? latestResponse.choiceIds ?? (latestResponse.choiceId ? [latestResponse.choiceId] : [])
            : [];
        const responseIsFreeform = latestResponse?.type === 'choiceResponse' && latestResponse.responseKind === 'freeform';
        const draft = promptChoiceDrafts[promptKey] ?? { selectedIds: [], useFreeform: false, freeformText: '' };
        const activeSelectedIds = isAnswered ? responseChoiceIds : draft.selectedIds;
        const freeformText =
          isAnswered && latestResponse?.type === 'choiceResponse' && responseIsFreeform ? latestResponse.text ?? '' : draft.freeformText;
        const minSelections = typeof block.minSelections === 'number' ? block.minSelections : selectionMode === 'multi' ? undefined : 1;
        const maxSelections = typeof block.maxSelections === 'number' ? block.maxSelections : selectionMode === 'single' ? 1 : undefined;
        const helperText = buildSelectionRuleText(selectionMode, minSelections, maxSelections);
        const isUsingFreeform = isAnswered ? responseIsFreeform : draft.useFreeform;
        const selectedCount = activeSelectedIds.length;
        const satisfiesMin = isUsingFreeform ? freeformText.trim().length > 0 : minSelections === undefined || selectedCount >= minSelections;
        const satisfiesMax = isUsingFreeform ? true : maxSelections === undefined || selectedCount <= maxSelections;
        const canSubmitDraft = satisfiesMin && satisfiesMax && (isUsingFreeform || selectedCount > 0);
        return (
          <div key={`${event.id}-block-${index}`} className="chat-prompt-card">
            <div className="chat-prompt-card__eyebrow">Choice prompt</div>
            <div className="chat-prompt-card__prompt">{block.prompt ?? 'Choose an option.'}</div>
            {helperText && <div className="chat-prompt-card__hint">{helperText}</div>}
            <div className="chat-prompt-card__choices">
              {choices.map((choice, choiceIndex) => (
                <label
                  key={`${promptKey}-choice-${choice.id ?? choiceIndex}`}
                  className={`chat-prompt-card__choice ${
                    choice.id && activeSelectedIds.includes(choice.id) ? 'chat-prompt-card__choice--selected' : ''
                  } ${
                    isUsingFreeform && !isAnswered && !isSuperseded ? 'chat-prompt-card__choice--muted' : ''
                  }`}>
                  <input
                    type={inputType}
                    className="chat-prompt-card__choice-input"
                    name={promptKey}
                    checked={Boolean(choice.id && activeSelectedIds.includes(choice.id))}
                    disabled={isAnswered || isSuperseded || isPending || !choice.id || (isUsingFreeform && !isAnswered && !isSuperseded)}
                    onChange={() => {
                      const choiceId = choice.id;
                      if (!choiceId) {
                        return;
                      }
                      if (selectionMode === 'single' && !block.allowFreeform) {
                        void submitSingleChoicePrompt(event, block, index, choice);
                        return;
                      }
                      setPromptChoiceDrafts((prev) => {
                        const current = prev[promptKey] ?? { selectedIds: [], useFreeform: false, freeformText: '' };
                        const selectedIds = current.selectedIds.includes(choiceId)
                          ? current.selectedIds.filter((value) => value !== choiceId)
                          : selectionMode === 'single'
                            ? [choiceId]
                            : [...current.selectedIds, choiceId];
                        return {
                          ...prev,
                          [promptKey]: {
                            ...current,
                            selectedIds,
                            useFreeform: false
                          }
                        };
                      });
                    }}
                  />
                  <span className="chat-prompt-card__choice-label">
                    <span>{choice.label ?? choice.id ?? `Option ${choiceIndex + 1}`}</span>
                    {choice.description && <small>{choice.description}</small>}
                  </span>
                </label>
              ))}
            </div>
            {block.allowFreeform && (
              <div className="chat-prompt-card__freeform">
                <label className="chat-prompt-card__checkbox">
                  <input
                    type="checkbox"
                    checked={isUsingFreeform}
                    disabled={isAnswered || isSuperseded || isPending}
                    onChange={(changeEvent) =>
                      setPromptChoiceDrafts((prev) => {
                        const current = prev[promptKey] ?? { selectedIds: [], useFreeform: false, freeformText: '' };
                        return {
                          ...prev,
                          [promptKey]: {
                            ...current,
                            useFreeform: changeEvent.target.checked,
                            selectedIds: changeEvent.target.checked ? [] : current.selectedIds
                          }
                        };
                      })
                    }
                  />
                  <span>{block.freeformLabel ?? 'Use a custom response'}</span>
                </label>
                <textarea
                  className="chat-prompt-card__input chat-prompt-card__input--multiline"
                  value={freeformText}
                  placeholder="Provide your custom response"
                  disabled={isAnswered || isSuperseded || isPending || !isUsingFreeform}
                  onChange={(changeEvent) =>
                    setPromptChoiceDrafts((prev) => {
                      const current = prev[promptKey] ?? { selectedIds: [], useFreeform: false, freeformText: '' };
                      return {
                        ...prev,
                        [promptKey]: {
                          ...current,
                          freeformText: changeEvent.target.value
                        }
                      };
                    })
                  }
                />
              </div>
            )}
            {(selectionMode === 'multi' || block.allowFreeform) && !isAnswered && !isSuperseded && (
              <div className="chat-prompt-card__actions">
                <button
                  type="button"
                  className="chat-prompt-card__submit"
                  disabled={isPending || !canSubmitDraft}
                  onClick={() => void submitChoicePromptDraft(event, block, index)}>
                  {isPending ? 'Sending…' : 'Submit choice'}
                </button>
              </div>
            )}
            {isAnswered && <div className="chat-prompt-card__state">Answered</div>}
            {isSuperseded && <div className="chat-prompt-card__state">Superseded by a newer prompt</div>}
            {submission?.error && <div className="chat-prompt-card__error">{submission.error}</div>}
          </div>
        );
      }

      if (block.type === 'confirmationPrompt') {
        const promptKey = getPromptKey(promptMessageId, block.questionId, block.type, index);
        const submission = promptSubmissionState[promptKey];
        const isAnswered = answeredPromptKeys.has(promptKey);
        const isSuperseded = supersededPromptKeys.has(promptKey);
        const isPending = Boolean(submission?.pending);
        return (
          <div key={`${event.id}-block-${index}`} className="chat-prompt-card chat-prompt-card--confirmation">
            <div className="chat-prompt-card__eyebrow">Confirmation</div>
            <div className="chat-prompt-card__prompt">{block.prompt ?? 'Please confirm.'}</div>
            <div className="chat-prompt-card__choices">
              <button
                type="button"
                className="chat-prompt-card__choice chat-prompt-card__choice--confirm"
                disabled={isAnswered || isSuperseded || isPending}
                onClick={() => void submitConfirmationPrompt(event, block, index, true)}>
                {block.confirmLabel ?? 'Confirm'}
              </button>
              <button
                type="button"
                className="chat-prompt-card__choice chat-prompt-card__choice--cancel"
                disabled={isAnswered || isSuperseded || isPending}
                onClick={() => void submitConfirmationPrompt(event, block, index, false)}>
                {block.cancelLabel ?? 'Cancel'}
              </button>
            </div>
            {isAnswered && <div className="chat-prompt-card__state">Answered</div>}
            {isSuperseded && <div className="chat-prompt-card__state">Superseded by a newer prompt</div>}
            {submission?.error && <div className="chat-prompt-card__error">{submission.error}</div>}
          </div>
        );
      }

      if (block.type === 'inputPrompt') {
        const promptKey = getPromptKey(promptMessageId, block.questionId, block.type, index);
        const submission = promptSubmissionState[promptKey];
        const isAnswered = answeredPromptKeys.has(promptKey);
        const isSuperseded = supersededPromptKeys.has(promptKey);
        const isPending = Boolean(submission?.pending);
        const inputMode = block.inputMode === 'multiline' ? 'multiline' : 'text';
        const draftValue = promptInputDrafts[promptKey] ?? '';
        return (
          <div key={`${event.id}-block-${index}`} className="chat-prompt-card">
            <div className="chat-prompt-card__eyebrow">Input prompt</div>
            <div className="chat-prompt-card__prompt">{block.prompt ?? 'Provide your response.'}</div>
            {block.label && <label className="chat-prompt-card__label">{block.label}</label>}
            {inputMode === 'multiline' ? (
              <textarea
                className="chat-prompt-card__input chat-prompt-card__input--multiline"
                value={draftValue}
                placeholder={block.placeholder ?? ''}
                disabled={isAnswered || isSuperseded || isPending}
                onChange={(changeEvent) =>
                  setPromptInputDrafts((prev) => ({
                    ...prev,
                    [promptKey]: changeEvent.target.value
                  }))
                }
              />
            ) : (
              <input
                className="chat-prompt-card__input"
                value={draftValue}
                placeholder={block.placeholder ?? ''}
                disabled={isAnswered || isSuperseded || isPending}
                onChange={(changeEvent) =>
                  setPromptInputDrafts((prev) => ({
                    ...prev,
                    [promptKey]: changeEvent.target.value
                  }))
                }
              />
            )}
            <div className="chat-prompt-card__actions">
              <button
                type="button"
                className="chat-prompt-card__submit"
                disabled={isAnswered || isSuperseded || isPending}
                onClick={() => void submitInputPrompt(event, block, index)}>
                {isPending ? 'Sending…' : 'Send reply'}
              </button>
            </div>
            {isAnswered && <div className="chat-prompt-card__state">Answered</div>}
            {isSuperseded && <div className="chat-prompt-card__state">Superseded by a newer prompt</div>}
            {submission?.error && <div className="chat-prompt-card__error">{submission.error}</div>}
          </div>
        );
      }

      if (block.type === 'formPrompt') {
        const promptKey = getPromptKey(promptMessageId, block.questionId, block.type, index);
        const submission = promptSubmissionState[promptKey];
        const isAnswered = answeredPromptKeys.has(promptKey);
        const isSuperseded = supersededPromptKeys.has(promptKey);
        const isPending = Boolean(submission?.pending);
        const fields = Array.isArray(block.fields) ? (block.fields as ChatFormFieldRecord[]) : [];
        const draft = promptFormDrafts[promptKey] ?? {};
        return (
          <div key={`${event.id}-block-${index}`} className="chat-prompt-card chat-prompt-card--form">
            <div className="chat-prompt-card__eyebrow">Form prompt</div>
            <div className="chat-prompt-card__prompt">{block.prompt ?? 'Provide the requested details.'}</div>
            <div className="chat-prompt-card__form">
              {fields.map((field, fieldIndex) => {
                const fieldId = field.id ?? `field-${fieldIndex}`;
                const fieldValue = draft[fieldId] ?? '';
                const multiline = field.fieldType === 'textarea';
                return (
                  <label key={`${promptKey}-${fieldId}`} className="chat-prompt-card__field">
                    <span>{field.label ?? fieldId}</span>
                    {field.helpText && <small>{field.helpText}</small>}
                    {multiline ? (
                      <textarea
                        className="chat-prompt-card__input chat-prompt-card__input--multiline"
                        value={fieldValue}
                        placeholder={field.placeholder ?? ''}
                        disabled={isAnswered || isSuperseded || isPending}
                        onChange={(changeEvent) =>
                          setPromptFormDrafts((prev) => ({
                            ...prev,
                            [promptKey]: {
                              ...(prev[promptKey] ?? {}),
                              [fieldId]: changeEvent.target.value
                            }
                          }))
                        }
                      />
                    ) : (
                      <input
                        className="chat-prompt-card__input"
                        value={fieldValue}
                        placeholder={field.placeholder ?? ''}
                        disabled={isAnswered || isSuperseded || isPending}
                        onChange={(changeEvent) =>
                          setPromptFormDrafts((prev) => ({
                            ...prev,
                            [promptKey]: {
                              ...(prev[promptKey] ?? {}),
                              [fieldId]: changeEvent.target.value
                            }
                          }))
                        }
                      />
                    )}
                  </label>
                );
              })}
            </div>
            <div className="chat-prompt-card__actions">
              <button
                type="button"
                className="chat-prompt-card__submit"
                disabled={isAnswered || isSuperseded || isPending}
                onClick={() => void submitFormPrompt(event, block, index)}>
                {isPending ? 'Sending…' : 'Submit form'}
              </button>
            </div>
            {isAnswered && <div className="chat-prompt-card__state">Answered</div>}
            {isSuperseded && <div className="chat-prompt-card__state">Superseded by a newer prompt</div>}
            {submission?.error && <div className="chat-prompt-card__error">{submission.error}</div>}
          </div>
        );
      }

      if (block.type === 'choiceResponse') {
        return (
          <div key={`${event.id}-block-${index}`} className="chat-response-chip-group">
            <span className="chat-response-chip">
              {block.label ?? ((block.labels ?? []).join(', ') || block.text || 'Choice submitted')}
            </span>
          </div>
        );
      }

      if (block.type === 'confirmationResponse') {
        const label =
          block.decision === 'confirm' ? 'Confirmed' : block.decision === 'cancel' ? 'Cancelled' : 'Confirmation submitted';
        return (
          <div key={`${event.id}-block-${index}`} className="chat-response-chip-group">
            <span className="chat-response-chip">{label}</span>
          </div>
        );
      }

      if (block.type === 'inputResponse') {
        return (
          <div key={`${event.id}-block-${index}`} className="chat-message__markdown">
            {renderMarkdown(block.text ?? '')}
          </div>
        );
      }

      if (block.type === 'formResponse') {
        const promptKey = getPromptKey(promptMessageId, block.questionId, block.type, index);
        const fields = Array.isArray(block.fields) ? (block.fields as ChatFormResponseFieldRecord[]) : [];
        return (
          <div key={`${event.id}-block-${index}`} className="chat-response-summary">
            {fields.map((field, fieldIndex) => {
              const values = formatFormResponseFieldValue(field);
              return (
                <div key={`${promptKey}-field-${field.fieldId ?? fieldIndex}`} className="chat-response-summary__row">
                  <span className="chat-response-summary__label">{field.fieldId ?? `Field ${fieldIndex + 1}`}</span>
                  <span className="chat-response-summary__value">{values}</span>
                </div>
              );
            })}
          </div>
        );
      }

      return null;
    })
    .filter(Boolean);

  if (nodes.length > 0) {
    return <>{nodes}</>;
  }

  const fallbackText = event.text?.trim() ?? '';
  if (!fallbackText) {
    return null;
  }
  return (
    <div className="chat-message__markdown">
      {renderMarkdown(fallbackText)}
    </div>
  );
}
